import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Chip,
  Stack,
  Autocomplete,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  FilterAltRounded as FilterAltRoundedIcon,
  TrendingDownOutlined as TrendingDownOutlinedIcon,
  Print as PrintIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext, emitirEcfDgii } from 'context';
import { tienePermisosModulo } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, StatCard, fpDataGridSx, fpInputSx } from 'components/financepro';
import VistaGastoElectronico from './VistaGastoElectronico';
import DetalleEcf from './DetalleEcf';
import { useEmpresaFiscal, useEmisorFiscal } from "lib/empresaFiscal";

/** Clave del programa para validar permisos (misma regla que FacturaPOS). */
export const PROGRAMA_GASTOS_ELECTRONICOS = 'gastoselectronicos';

/** Solo e-CF E43 (Gastos Menores Electrónico). */
const CODIGOS_DOCUMENTO_GASTOS = ['43'];

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const codigoTipoDocumento = (tipo) => {
  const desdeCodigo = String(tipo?.codigo || '').replace(/\D/g, '');
  if (desdeCodigo) return desdeCodigo;
  return (tipo?.nombre || '').split(':')[1]?.replace(/\D/g, '') || '';
};

const toNumber = (value) => parseFloat(String(value ?? '').replace(/,/g, '')) || 0;

const formatMoney = (value) =>
  (Number(value) || 0).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** RNC/cédula DGII válido (9 u 11 dígitos). Vacío si no aplica (p. ej. 999999). */
const rncCompradorValido = (valor) => {
  const digits = String(valor || '').replace(/\D/g, '');
  if (!digits || digits === '999999') return '';
  if (digits.length === 9 || digits.length === 11) return digits;
  return '';
};

const fechaDdMmYyyy = (valor) => {
  const texto = String(valor || '').trim();
  if (!texto) return '';
  const iso = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const dmy = texto.match(/^(\d{2})[/-](\d{2})[/-](\d{4})/);
  if (dmy) return `${dmy[1]}-${dmy[2]}-${dmy[3]}`;
  return texto;
};

const detalleRespuestaEcf = (emision) => {
  const rd = emision?.respuesta_dgii || {};
  const deMensajes = (rd.mensajes || [])
    .map((m) => {
      if (typeof m === 'string') return m;
      const texto = m.valor || m.mensaje || m.descripcion || '';
      return `${texto}${m.codigo ? ` (${m.codigo})` : ''}`;
    })
    .filter((t) => String(t || '').trim());
  const extra = [rd.error, rd.detalle, rd.mensaje, rd.descripcion].filter((t) => String(t || '').trim());
  const xsd = (emision?.xsd_mensajes || []).filter(
    (m) => m && !String(m).toLowerCase().includes('no instalado')
  );
  return [...deMensajes, ...extra, ...xsd]
    .filter((t, i, arr) => t && arr.indexOf(t) === i)
    .join(' · ');
};

/** Primer RNC de comprador válido entre los gastos seleccionados, si existe. */
const rncDesdeGastos = (gastos) => {
  for (const g of gastos || []) {
    const rnc = rncCompradorValido(g.rnc || g.rnc_comprador || g.cedula);
    if (rnc) return rnc;
  }
  return '';
};

/**
 * Resume gastos ya firmados en una fila por e-CF (suma valor y cuenta líneas).
 * Los pendientes (sin comprobante) se dejan uno a uno para poder firmarlos.
 */
const resumirGastosPorEcf = (list) => {
  const porEcf = new Map();
  for (const r of list || []) {
    const ncf = String(r.comprobantefiscal || '').trim();
    const firmado = String(r.marcado_electronico || '').trim().toUpperCase() === 'S';
    if (!ncf || !firmado) continue;
    if (!porEcf.has(ncf)) {
      porEcf.set(ncf, {
        ...r,
        id: `ecf-${ncf}`,
        id_gasto: r.id,
        ids: [r.id],
        cantidad_gastos: 1,
        valor: toNumber(r.valor),
        descripciones: [String(r.descripcion || '').trim()].filter(Boolean),
        categorias: [String(r.tipogastos || '').trim()].filter(Boolean),
        sucursales: [String(r.sucursal || '').trim()].filter(Boolean),
        gastosDetalle: [r],
        esResumenEcf: true,
      });
    } else {
      const g = porEcf.get(ncf);
      g.ids.push(r.id);
      g.cantidad_gastos += 1;
      g.valor += toNumber(r.valor);
      g.gastosDetalle.push(r);
      const desc = String(r.descripcion || '').trim();
      if (desc) g.descripciones.push(desc);
      const cat = String(r.tipogastos || '').trim();
      if (cat && !g.categorias.includes(cat)) g.categorias.push(cat);
      const suc = String(r.sucursal || '').trim();
      if (suc && !g.sucursales.includes(suc)) g.sucursales.push(suc);
    }
  }

  const vistos = new Set();
  const salida = [];
  for (const r of list || []) {
    const ncf = String(r.comprobantefiscal || '').trim();
    const firmado = String(r.marcado_electronico || '').trim().toUpperCase() === 'S';
    if (!ncf || !firmado) {
      salida.push({ ...r, cantidad_gastos: 1, esResumenEcf: false, gastosDetalle: [r] });
      continue;
    }
    if (vistos.has(ncf)) continue;
    vistos.add(ncf);
    const g = porEcf.get(ncf);
    const preview = g.descripciones.slice(0, 2).join('; ');
    salida.push({
      ...g,
      tipogastos: g.categorias.join(', ') || g.tipogastos,
      sucursal: g.sucursales.length <= 1 ? (g.sucursales[0] || g.sucursal || '') : g.sucursales.join(', '),
      descripcion:
        g.cantidad_gastos === 1
          ? (g.descripciones[0] || g.descripcion || '')
          : `${g.cantidad_gastos} gastos${preview ? `: ${preview}` : ''}${g.descripciones.length > 2 ? '…' : ''}`,
    });
  }
  return salida;
};


/** Convierte YYYY-MM-DD / ISO / Date a dd/mm/aaaa. Si ya viene dd/mm/aaaa, la deja igual. */
const formatFecha = (fecha) => {
  if (fecha == null || fecha === '') return '';
  if (fecha instanceof Date && !Number.isNaN(fecha.getTime())) {
    const d = String(fecha.getDate()).padStart(2, '0');
    const m = String(fecha.getMonth() + 1).padStart(2, '0');
    const y = fecha.getFullYear();
    return `${d}/${m}/${y}`;
  }
  const s = String(fecha).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const dmyDash = s.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (dmyDash) return `${dmyDash[1]}/${dmyDash[2]}/${dmyDash[3]}`;
  // Si el backend devolvió literalmente el patrón de MySQL por error de DATE_FORMAT.
  if (s === '%Y-%m-%d' || s === '%%Y-%%m-%%d') return '';
  return s;
};

const GastosElectronicos = () => {
  // Multi-empresa: empresa fiscal elegida en la barra lateral (1 sin el modo).
  const { empresa: empresaFiscal } = useEmpresaFiscal();
  const emisorFiscal = useEmisorFiscal();
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, claveLog, roleDetalles, comprobanteelectronico, nombreNegocio, rnc_empresa, direccion_empresa } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = useMemo(
    () =>
      tienePermisosModulo(roleDetalles, PROGRAMA_GASTOS_ELECTRONICOS) ||
      tienePermisosModulo(roleDetalles, 'gastos'),
    [roleDetalles]
  );

  const puedeModificar = useMemo(
    () =>
      tienePermisosModulo(roleDetalles, PROGRAMA_GASTOS_ELECTRONICOS) ||
      tienePermisosModulo(roleDetalles, 'gastos'),
    [roleDetalles]
  );

  const hoy = new Date().toISOString().slice(0, 10);
  const [fechaDesde, setFechaDesde] = useState(hoy.slice(0, 8) + '01');
  const [fechaHasta, setFechaHasta] = useState(hoy);
  const [sucursalList, setSucursalList] = useState([]);
  const [sucursal, setSucursal] = useState(null);

  const [rows, setRows] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [filtroEcf, setFiltroEcf] = useState('pendientes');

  const [tiposDocumentosFiscales, setTiposDocumentosFiscales] = useState([]);
  const [tipoDocumento, setTipoDocumento] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [vistaGastoOpen, setVistaGastoOpen] = useState(false);
  const [vistaGastoData, setVistaGastoData] = useState({
    gastos: [],
    ncf: '',
    emisionData: {},
    rncComprador: '',
  });
  const [detalleEcfAbierto, setDetalleEcfAbierto] = useState(false);
  const [gastoDetalleEcf, setGastoDetalleEcf] = useState(null);
  const [reimprimiendoNcf, setReimprimiendoNcf] = useState('');

  const docFiscalSeleccionado = useMemo(
    () => tiposDocumentosFiscales.find((t) => t.nombre === tipoDocumento) || null,
    [tiposDocumentosFiscales, tipoDocumento]
  );

  const gastosSeleccionados = useMemo(
    () => rows.filter((r) => selectionModel.includes(r.id)),
    [rows, selectionModel]
  );

  const rowsGrid = useMemo(() => resumirGastosPorEcf(rows), [rows]);

  const totalLista = useMemo(
    () => rowsGrid.reduce((sum, g) => sum + toNumber(g.valor), 0),
    [rowsGrid]
  );

  const totalSeleccionado = useMemo(
    () => gastosSeleccionados.reduce((sum, g) => sum + toNumber(g.valor), 0),
    [gastosSeleccionados]
  );

  const cargarSucursales = useCallback(async () => {
    if (!usuarioLog) return;
    try {
      const response = await fetch(`${servidor}/api/recuperasucursales`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: claveLog, empresa: empresaFiscal, usuario: usuarioLog }),
      });
      if (response.ok) {
        const data = await response.json();
        setSucursalList(data.sucursales || []);
      }
    } catch (_e) {
      /* silencioso: el filtro de sucursal queda vacío */
    }
  }, [usuarioLog, claveLog, servidor]);

  const cargarTiposDocumentosFiscales = useCallback(async () => {
    if (!usuarioLog) return;
    try {
      const response = await fetch(`${servidor}/api/facturabackend_listartiposdocumentos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '', empresa: empresaFiscal,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        const tiposActivos = (data.tipos || []).filter(
          (tipo) =>
            tipo.activo === 'S' &&
            CODIGOS_DOCUMENTO_GASTOS.includes(codigoTipoDocumento(tipo))
        );
        setTiposDocumentosFiscales(tiposActivos);
        if (tiposActivos.length > 0) {
          setTipoDocumento((prev) => prev || tiposActivos[0].nombre);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al cargar tipos de documentos fiscales');
      }
    } catch (_e) {
      showError('Error de conexión al cargar tipos de documentos fiscales');
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor, showError]);

  useEffect(() => {
    if (!puedeLeer) return;
    cargarSucursales();
    cargarTiposDocumentosFiscales();
  }, [puedeLeer, cargarSucursales, cargarTiposDocumentosFiscales]);

  const buscarGastos = useCallback(async () => {
    if (!puedeLeer) {
      showError('No tiene permisos para consultar gastos electrónicos.');
      return;
    }
    if (!fechaDesde || !fechaHasta) {
      showError('Seleccione un rango de fechas');
      return;
    }
    if (fechaDesde > fechaHasta) {
      showError('La fecha desde no puede ser mayor que la fecha hasta');
      return;
    }

    const sucCode = sucursal?.label
      ? String(sucursal.label).split('-')[0]
      : sucursal
        ? String(sucursal).split('-')[0]
        : 'todo';

    setCargando(true);
    setSelectionModel([]);
    try {
      const response = await fetch(
        `${servidor}/api/facturaselectronicasdgii_listargastospendientes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            usuario: usuarioLog || '',
            password: claveLog || '', empresa: empresaFiscal,
            fechadesde: fechaDesde,
            fechahasta: fechaHasta,
            sucursal: sucCode || 'todo',
            filtro_ecf: filtroEcf,
          }),
        }
      );
      if (response.ok) {
        const data = await response.json();
        const list = (data.data || []).map((r) => ({
          ...r,
          id: r.id,
          fecha: formatFecha(r.fecha),
          fecha_ecf: formatFecha(r.fecha_ecf || r.fecha_firma_ecf || ''),
          valor: toNumber(r.valor),
        }));
        setRows(list);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setRows([]);
        showError(errorData.Error || 'No se pudieron cargar los gastos');
      }
    } catch (_e) {
      setRows([]);
      showError('Error de conexión al cargar gastos');
    } finally {
      setCargando(false);
    }
  }, [
    puedeLeer,
    fechaDesde,
    fechaHasta,
    sucursal,
    filtroEcf,
    servidor,
    usuarioLog,
    claveLog,
    showError,
  ]);

  useEffect(() => {
    if (!puedeLeer || !fechaDesde || !fechaHasta) return undefined;
    buscarGastos();
    return undefined;
  }, [filtroEcf]);

  const descargarArchivoECF = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'gastos_ecf.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const descargarXmlECF = (xmlTexto, filename) => {
    descargarArchivoECF(new Blob([xmlTexto], { type: 'text/xml' }), filename);
  };

  const limpiarSeleccion = () => {
    setSelectionModel([]);
  };

  const abrirConfirmacion = () => {
    if (!puedeModificar) {
      showError('No tiene permisos para firmar gastos electrónicos.');
      return;
    }
    if (gastosSeleccionados.length === 0) {
      showError('Debe seleccionar al menos un gasto');
      return;
    }
    if (!tipoDocumento || !docFiscalSeleccionado) {
      showError(
        tiposDocumentosFiscales.length === 0
          ? 'No existe un comprobante fiscal electrónico E43 activo en tipos de documentos fiscales. Debe configurarlo primero.'
          : 'Debe seleccionar el tipo de documento fiscal (E43)'
      );
      return;
    }
    if (comprobanteelectronico !== 'S') {
      showError('La empresa no tiene habilitada la emisión de comprobantes electrónicos');
      return;
    }
    setConfirmOpen(true);
  };

  const firmarGastos = async () => {
    if (!puedeModificar) {
      showError('No tiene permisos para firmar gastos electrónicos.');
      return;
    }
    setConfirmOpen(false);
    setGuardando(true);
    try {
      // El backend genera/valida el e-CF primero; solo si es correcto marca los gastos.
      const response = await fetch(
        `${servidor}/api/facturaselectronicasdgii_guardargastoselectronicos`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            usuario: usuarioLog || '',
            password: claveLog || '', empresa: empresaFiscal,
            ids: gastosSeleccionados.map((g) => g.id),
            tipo_documento: tipoDocumento,
            comprobanteelectronico: comprobanteelectronico === 'S' ? 'S' : 'N',
            codigocomprobante:
              codigoTipoDocumento(docFiscalSeleccionado) || '43',
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        showError(
          errorData.Error ||
            'No se pudo generar el e-CF; los gastos no fueron guardados'
        );
        return;
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      let filename = 'gastos_ecf.xlsx';
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match && match[1]) filename = match[1];
      const ncf = response.headers.get('X-Comprobante-Fiscal') || '';
      const cantidad = response.headers.get('X-Cantidad-Gastos') || String(gastosSeleccionados.length);
      const rncComprador = rncDesdeGastos(gastosSeleccionados);
      const fechaVencimiento = fechaDdMmYyyy(response.headers.get('X-Fecha-Valida') || '');

      descargarArchivoECF(blob, filename);

      const payloadEmision = {
        comprobanteelectronico,
        rnc: rncComprador,
        razonsocial: '',
        comprobante: ncf,
        tipocomprobante: tipoDocumento,
        codigocomprobante: codigoTipoDocumento(docFiscalSeleccionado) || '43',
        fechavencimientosecuencia: fechaVencimiento,
        items: gastosSeleccionados.map((g) => ({
          item: (g.descripcion || g.tipogastos || 'Gasto').trim(),
          cantidad: 1,
          valor: toNumber(g.valor),
          descuento: 0,
          tasaitbis: 0,
        })),
        password: claveLog, empresa: empresaFiscal,
        usuario: usuarioLog,
        rncemisor: emisorFiscal.rnc || '',
        razonsocialemisor: emisorFiscal.nombre || '',
        direccionemisor: emisorFiscal.direccion || '',
      };

      // emitirEcfDgii firma y SIEMPRE registra en ecf_emitidos (también si la
      // DGII rechaza o el envío queda en error). La vista de impresión solo
      // se abre cuando el e-CF no fue rechazado/error.
      const emision = await emitirEcfDgii(servidor, payloadEmision);
      const estadoEcf = String(emision?.estado || '').trim().toLowerCase();
      const dgiiFallido = estadoEcf === 'rechazado' || estadoEcf === 'error';
      const idsAfectados = new Set(gastosSeleccionados.map((g) => g.id));

      const sincronizarEstadoGastos = async (estado, fechaFirma = '') => {
        if (!ncf || !estado) return;
        try {
          await fetch(`${servidor}/api/facturaselectronicasdgii_actualizarestadoecf`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              usuario: usuarioLog || '',
              password: claveLog || '', empresa: empresaFiscal,
              comprobante: ncf,
              estado,
              fecha_firma: fechaFirma,
            }),
          });
        } catch (_ignored) { /* best-effort: no toca ecf_emitidos */ }
      };

      const devolverGastosAPendientes = () => {
        setRows((prev) =>
          prev
            .map((r) =>
              idsAfectados.has(r.id)
                ? { ...r, marcado_electronico: 'N', comprobantefiscal: '', estado_ecf: '' }
                : r
            )
            .filter((r) => (filtroEcf === 'firmados' ? !idsAfectados.has(r.id) : true))
        );
        setSelectionModel([]);
      };

      if (emision.ok) {
        descargarXmlECF(emision.xml, `${emision.encf || 'ecf'}.xml`);
        if (emision.xml_rfce) {
          descargarXmlECF(emision.xml_rfce, `${emision.encf || 'ecf'}-resumen.xml`);
        }
        const etiquetas = {
          aceptado: 'aceptado por la DGII',
          aceptado_condicional: 'aceptado condicional por la DGII',
          en_proceso: `en proceso en la DGII${emision.track_id ? ` (TrackId ${emision.track_id})` : ''}`,
          rechazado: 'RECHAZADO por la DGII',
          pendiente_envio: 'firmado; DGII inaccesible, se reenviará',
          error: 'firmado; el envío a la DGII falló',
        };
        const detalleDgii = detalleRespuestaEcf(emision);
        const mensaje =
          `e-CF ${emision.encf} ${etiquetas[estadoEcf] || emision.estado}` +
          ` · código ${emision.codigo_seguridad}` +
          (detalleDgii ? ` — ${detalleDgii}` : '');
        if (dgiiFallido) {
          showError(mensaje);
        } else {
          showSuccess(
            ncf
              ? `${mensaje} · asignado a ${cantidad} gasto(s)`
              : mensaje
          );
        }
        await sincronizarEstadoGastos(estadoEcf || 'firmado', emision.fecha_firma || emision.fecha || '');
      } else {
        showError(emision.error || 'No se pudo emitir el e-CF ante la DGII');
        await sincronizarEstadoGastos('error');
      }

      if (!emision.ok || dgiiFallido) {
        devolverGastosAPendientes();
        return;
      }

      setVistaGastoData({
        gastos: [...gastosSeleccionados],
        ncf,
        emisionData: {
          ...emision,
          fechavencimientosecuencia: fechaVencimiento,
          fechavencimiento: fechaVencimiento,
        },
        rncComprador,
      });
      setVistaGastoOpen(true);

      if (filtroEcf === 'todos') {
        setRows((prev) =>
          prev.map((r) =>
            idsAfectados.has(r.id)
              ? { ...r, marcado_electronico: 'S', comprobantefiscal: ncf, estado_ecf: estadoEcf }
              : r
          )
        );
      } else {
        setRows((prev) => prev.filter((r) => !idsAfectados.has(r.id)));
      }
      setSelectionModel([]);
    } catch (_e) {
      showError('Error de conexión al firmar gastos electrónicos');
    } finally {
      setGuardando(false);
    }
  };

  const abrirDetalleFirma = useCallback((row) => {
    const ncf = String(row?.comprobantefiscal || '').trim();
    if (!ncf) {
      showError('Este gasto no tiene e-CF para consultar la firma.');
      return;
    }
    setGastoDetalleEcf({
      ...row,
      encf: ncf,
      ncf,
      nfact: row.id_gasto || row.ids?.[0] || row.id,
      tipofactura: 'Gastos Menores Electrónico',
      nombre_cliente: row.descripcion || row.tipogastos || '',
      monto_total: toNumber(row.valor),
      fecha_factura: row.fecha,
      estado: row.estado_ecf,
      fecha_firma: row.fecha_firma_ecf || '',
    });
    setDetalleEcfAbierto(true);
  }, [showError]);

  const reimprimirGasto = useCallback(async (row) => {
    const ncf = String(row?.comprobantefiscal || '').trim();
    if (!ncf) {
      showError('Este gasto no tiene e-CF para reimprimir.');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    setReimprimiendoNcf(ncf);
    try {
      const response = await fetch(
        `${servidor}/api/facturaselectronicasdgii_obtenergastoelectronico`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            usuario: usuarioLog || '',
            password: claveLog || '', empresa: empresaFiscal,
            comprobante: ncf,
          }),
        }
      );
      const data = await response.json().catch(() => ({}));
      const detalleLocal = Array.isArray(row?.gastosDetalle) && row.gastosDetalle.length
        ? row.gastosDetalle
        : [row].filter((g) => !g.esResumenEcf);
      if (!response.ok) {
        if (detalleLocal.length === 0) {
          showError(data.Error || 'No se pudo cargar el gasto electrónico');
          return;
        }
      }
      const emision = data.emision || {};
      const rncDigits = String(emision.rnc_comprador || row.rnc || row.rnc_comprador || '').replace(/\D/g, '');
      const rncComprador =
        rncDigits &&
        rncDigits !== '999999' &&
        (rncDigits.length === 9 || rncDigits.length === 11)
          ? rncDigits
          : '';
      const gastosImpresion =
        Array.isArray(data.gastos) && data.gastos.length > 0 ? data.gastos : detalleLocal;
      if (!gastosImpresion.length) {
        showError('No hay detalle de gastos para reimprimir este e-CF');
        return;
      }
      setVistaGastoData({
        gastos: gastosImpresion,
        ncf: data.ncf || ncf,
        emisionData: {
          ...emision,
          codigo_seguridad: emision.codigo_seguridad || '',
          fecha_firma: emision.fecha_firma || row.fecha_firma_ecf || '',
          estado: emision.estado || row.estado_ecf || '',
          encf: emision.encf || ncf,
        },
        rncComprador,
      });
      setVistaGastoOpen(true);
    } catch (_e) {
      const detalleLocal = Array.isArray(row?.gastosDetalle) && row.gastosDetalle.length
        ? row.gastosDetalle
        : [];
      if (detalleLocal.length > 0) {
        setVistaGastoData({
          gastos: detalleLocal,
          ncf,
          emisionData: {
            codigo_seguridad: '',
            fecha_firma: row.fecha_firma_ecf || '',
            estado: row.estado_ecf || '',
            encf: ncf,
          },
          rncComprador: rncDesdeGastos(detalleLocal),
        });
        setVistaGastoOpen(true);
      } else {
        showError('Error de conexión al reimprimir el gasto electrónico');
      }
    } finally {
      setReimprimiendoNcf('');
    }
  }, [usuarioLog, claveLog, servidor, showError]);

  const columns = useMemo(
    () => {
      const cols = [
      {
        field: 'id',
        headerName: 'ID',
        width: 80,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) =>
          params.row?.esResumenEcf ? '—' : params.row?.id_gasto || params.value,
      },
      {
        field: 'cantidad_gastos',
        headerName: 'Gastos',
        width: 90,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (_value, row) => row?.cantidad_gastos || 1,
      },
      {
        field: 'fecha',
        headerName: 'Fecha gasto',
        width: 120,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value) => formatFecha(value),
      },
      {
        field: 'fecha_ecf',
        headerName: 'Fecha firma',
        width: 130,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (_value, row) => formatFecha(row?.fecha_ecf || row?.fecha_firma_ecf || ''),
      },
      {
        field: 'sucursal',
        headerName: 'Sucursal',
        width: 110,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'tipogastos',
        headerName: 'Categoría',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'descripcion',
        headerName: 'Descripción',
        flex: 2,
        minWidth: 220,
      },
      {
        field: 'valor',
        headerName: 'Valor',
        type: 'number',
        width: 140,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => `$${formatMoney(value)}`,
      },
      {
        field: 'comprobantefiscal',
        headerName: 'e-CF',
        width: 180,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const ncf = String(params.row?.comprobantefiscal || params.value || '').trim();
          return ncf || '—';
        },
      },
      {
        field: 'estado_ecf',
        headerName: 'Estado',
        width: 150,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const marcado = String(params.row?.marcado_electronico || '').trim().toUpperCase() === 'S';
          const estado = String(params.row?.estado_ecf || params.value || '').trim().toLowerCase();
          if (!estado && !marcado) return '—';
          const colorMap = {
            aceptado: 'success',
            aceptado_condicional: 'warning',
            rechazado: 'error',
            en_proceso: 'info',
            pendiente_envio: 'default',
            firmado: 'info',
            error: 'error',
          };
          const labelMap = {
            aceptado: 'Aceptado',
            aceptado_condicional: 'Condicional',
            rechazado: 'Rechazado',
            en_proceso: 'En proceso',
            pendiente_envio: 'Pendiente envío',
            firmado: 'Firmado',
            error: 'Error',
          };
          const clave = estado || 'firmado';
          return (
            <Chip
              size="small"
              label={labelMap[clave] || clave}
              color={colorMap[clave] || 'default'}
              sx={{ fontWeight: 700, fontSize: '0.75rem' }}
            />
          );
        },
      },
      {
        field: 'acciones',
        headerName: 'Acciones',
        width: 120,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const ncf = String(params.row?.comprobantefiscal || '').trim();
          const firmado = String(params.row?.marcado_electronico || '').trim().toUpperCase() === 'S';
          const reimprimiendo = reimprimiendoNcf && reimprimiendoNcf === ncf;
          return (
            <Stack direction="row" spacing={0.25} justifyContent="center">
              <Tooltip title={ncf ? 'Ver datos de la firma' : 'Sin e-CF firmado'}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!ncf || !usuarioLog}
                    onClick={() => abrirDetalleFirma(params.row)}
                    sx={{
                      color: FP.color.navy,
                      '&:hover': { bgcolor: FP.color.surfaceAlt },
                      '&.Mui-disabled': { color: FP.color.textMuted },
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={ncf && firmado ? 'Reimprimir gasto electrónico' : 'Sin e-CF para reimprimir'}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!ncf || !firmado || !usuarioLog || !!reimprimiendoNcf}
                    onClick={() => reimprimirGasto(params.row)}
                    sx={{
                      color: FP.color.accentBlue,
                      '&:hover': { bgcolor: FP.color.accentBlueSoft },
                      '&.Mui-disabled': { color: FP.color.textMuted },
                    }}
                  >
                    {reimprimiendo ? (
                      <CircularProgress size={16} sx={{ color: FP.color.accentBlue }} />
                    ) : (
                      <PrintIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        },
      },
    ];
      return filtroEcf === 'pendientes'
        ? cols.filter((c) => c.field !== 'cantidad_gastos')
        : cols;
    },
    [reimprimiendoNcf, usuarioLog, abrirDetalleFirma, reimprimirGasto, filtroEcf]
  );

  if (!puedeLeer) {
    return (
      <Box
        component="main"
        sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}
      >
        <PageHeader
          title="Gastos"
          subtitle="Gastos electrónicos — selección por sucursal y firma e-CF"
        />
        <Card sx={{ ...cardShellSx, maxWidth: 560, mx: 'auto', mt: 4 }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <ReceiptIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1.05rem' }}>
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, mt: 0.5, fontSize: '0.92rem' }}>
              No tienes permisos para acceder a gastos electrónicos.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Gastos"
        subtitle="Gastos electrónicos — selección por sucursal y firma e-CF (E43)"
        cta={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={limpiarSeleccion}
              disabled={!puedeModificar || selectionModel.length === 0 || guardando}
              sx={{
                textTransform: 'none',
                borderRadius: 1.4,
                fontWeight: 700,
                borderColor: FP.color.border,
                color: FP.color.textPrimary,
                bgcolor: '#FFFFFF',
                '&:hover': { borderColor: FP.color.borderStrong, bgcolor: FP.color.surfaceAlt },
              }}
            >
              Limpiar selección
            </Button>
            <Button
              variant="contained"
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <DescriptionIcon />}
              onClick={abrirConfirmacion}
              disabled={
                !puedeModificar ||
                guardando ||
                gastosSeleccionados.length === 0 ||
                !tipoDocumento ||
                !docFiscalSeleccionado ||
                tiposDocumentosFiscales.length === 0 ||
                comprobanteelectronico !== 'S'
              }
              sx={{
                bgcolor: FP.color.navy,
                color: '#FFFFFF',
                borderRadius: 1.4,
                px: 2.2,
                height: 38,
                boxShadow: 'none',
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
              }}
            >
              Firmar e-CF
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              label="Seleccionados"
              value={String(gastosSeleccionados.length)}
              icon={CheckCircleIcon}
              accent={FP.color.navy}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              label="Total a firmar"
              value={`$${formatMoney(totalSeleccionado)}`}
              icon={TrendingDownOutlinedIcon}
              accent={FP.color.warning}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              label={filtroEcf === 'firmados' ? 'e-CF en lista' : 'Gastos en lista'}
              value={String(filtroEcf === 'firmados' ? rowsGrid.length : rows.length)}
              icon={ReceiptIcon}
              accent={FP.color.accentBlue}
            />
          </Grid>
        </Grid>

        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FilterAltRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Filtros y documento fiscal
            </Typography>
            <Typography
              sx={{
                ml: 'auto',
                color: FP.color.textSecondary,
                fontSize: '0.78rem',
                fontWeight: 600,
              }}
            >
              Sucursales · E43 · {filtroEcf === 'firmados' ? 'Firmados' : filtroEcf === 'todos' ? 'Todos' : 'Pendientes'}
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label={filtroEcf === 'firmados' ? 'Firma desde' : 'Fecha Desde'}
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText={filtroEcf === 'firmados' ? 'Filtra por fecha de firma del e-CF' : ' '}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label={filtroEcf === 'firmados' ? 'Firma hasta' : 'Fecha Hasta'}
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText={filtroEcf === 'firmados' ? 'Filtra por fecha de firma del e-CF' : ' '}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  disablePortal
                  options={sucursalList || []}
                  value={sucursal}
                  onChange={(_e, value) => setSucursal(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sucursal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      placeholder="Todas"
                      sx={fpInputSx}
                    />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Filtro e-CF"
                  value={filtroEcf}
                  onChange={(e) => setFiltroEcf(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  <MenuItem value="pendientes">Pendientes de firmar</MenuItem>
                  <MenuItem value="firmados">Ya firmados</MenuItem>
                  <MenuItem value="todos">Todos</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Documento fiscal"
                  value={tipoDocumento}
                  onChange={(e) => setTipoDocumento(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                  disabled={!puedeModificar}
                >
                  <MenuItem value="">Seleccione…</MenuItem>
                  {tiposDocumentosFiscales.map((t) => (
                    <MenuItem key={t.id || t.nombre} value={t.nombre}>
                      {t.nombre}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={12}>
                <Button
                  variant="contained"
                  startIcon={cargando ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                  onClick={buscarGastos}
                  disabled={cargando || !usuarioLog}
                  sx={{
                    height: 40,
                    px: 3,
                    bgcolor: FP.color.navy,
                    color: '#FFFFFF',
                    borderRadius: 1.4,
                    boxShadow: 'none',
                    textTransform: 'none',
                    fontWeight: 700,
                    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                  }}
                >
                  Buscar gastos
                </Button>
              </Grid>
            </Grid>

            {comprobanteelectronico !== 'S' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                La empresa no tiene habilitada la emisión de comprobantes electrónicos. No podrá firmar el e-CF.
              </Alert>
            )}
            {tiposDocumentosFiscales.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No hay un tipo de documento fiscal E43 (Gastos Menores) activo. Configurelo en tipos de documentos fiscales.
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card elevation={0} sx={cardShellSx}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              {filtroEcf === 'firmados'
                ? 'e-CF firmados (resumen)'
                : filtroEcf === 'todos'
                  ? 'Todos los gastos'
                  : 'Gastos pendientes de e-CF'}
            </Typography>
            <Chip
              size="small"
              label={`${selectionModel.length} seleccionados`}
              sx={{
                ml: { xs: 0, sm: 1 },
                fontWeight: 700,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
              }}
            />
            <Typography
              sx={{
                ml: 'auto',
                color: FP.color.textSecondary,
                fontSize: '0.85rem',
                fontWeight: 700,
              }}
            >
              Total: ${formatMoney(filtroEcf === 'firmados' ? totalLista : totalSeleccionado)}
            </Typography>
          </Box>
          <Box sx={{ height: 480, width: '100%', p: { xs: 1.5, md: 2 } }}>
            <DataGrid
              rows={rowsGrid}
              columns={columns}
              checkboxSelection={filtroEcf !== 'firmados'}
              disableRowSelectionOnClick
              isRowSelectable={(params) =>
                !params.row?.esResumenEcf &&
                String(params.row.marcado_electronico || 'N').trim().toUpperCase() !== 'S'
              }
              loading={cargando}
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={(ids) => {
                if (!puedeModificar) return;
                setSelectionModel(Array.isArray(ids) ? ids : []);
              }}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              rowHeight={44}
              sx={fpDataGridSx}
            />
          </Box>
        </Card>
      </Box>

      <Dialog open={confirmOpen} onClose={() => !guardando && setConfirmOpen(false)}>
        <DialogTitle sx={{ fontWeight: 700, color: FP.color.textPrimary }}>
          Confirmar firma electrónica
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: FP.color.textSecondary }}>
            Se generará el e-CF <strong>{tipoDocumento}</strong> para{' '}
            <strong>{gastosSeleccionados.length}</strong> gasto(s) por un total de{' '}
            <strong>${formatMoney(totalSeleccionado)}</strong>. Solo si el
            comprobante es correcto se marcarán los gastos y no podrán volver a
            seleccionarse.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={guardando}
            sx={{ textTransform: 'none', fontWeight: 700, color: FP.color.textSecondary }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={firmarGastos}
            disabled={guardando}
            startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <DescriptionIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: FP.color.navy,
              '&:hover': { bgcolor: FP.color.navyDeep },
            }}
          >
            Firmar y descargar
          </Button>
        </DialogActions>
      </Dialog>

      <VistaGastoElectronico
        open={vistaGastoOpen}
        onClose={() => setVistaGastoOpen(false)}
        gastos={vistaGastoData.gastos}
        ncf={vistaGastoData.ncf}
        emisionData={vistaGastoData.emisionData}
        rncComprador={vistaGastoData.rncComprador}
        empresa={{
          nombre: emisorFiscal.nombre || '',
          direccion: emisorFiscal.direccion || '',
          rnc: emisorFiscal.rnc || '',
        }}
      />

      <DetalleEcf
        open={detalleEcfAbierto}
        onClose={() => {
          setDetalleEcfAbierto(false);
          setGastoDetalleEcf(null);
        }}
        encf={gastoDetalleEcf?.ncf || ''}
        factura={gastoDetalleEcf}
        onEstadoActualizado={(actualizado) => {
          const encfActualizado = actualizado?.encf;
          if (!encfActualizado) return;
          setRows((prev) =>
            prev.map((item) =>
              item.comprobantefiscal === encfActualizado
                ? { ...item, estado_ecf: actualizado.estado || item.estado_ecf }
                : item
            )
          );
          setGastoDetalleEcf((prev) =>
            prev && prev.ncf === encfActualizado
              ? { ...prev, estado: actualizado.estado || prev.estado, estado_ecf: actualizado.estado }
              : prev
          );
        }}
      />
    </Box>
  );
};

export default GastosElectronicos;
